import { observable, action, computed, makeObservable, runInAction } from 'mobx';
import { getErrorByCode } from 'src/domains/layouts/webview/components/common/errorMessage/errors';
import { CustomKeyboardState } from 'src/domains/players/state/CustomKeyboardState';
import { PromoCodesState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/promoCodesState/PromoCodes.state';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    checkPassword,
    validateEmail,
    validateEmailRaw,
    validatePassword,
    validateRequire,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { onValidateEmail } from 'src/domains/players/webview/components/SignUp/signupParts/emailValidation/EmailValidation';

export interface WelcomeStepFormType {
    email: string;
    password: string;
    promoCode: string;
    affiliateId?: string;
}

export class SignUpWelcomeState {
    @observable public isPromoCode: boolean;
    @observable public isShowPassword = false;
    @observable public submitErrors: string | null = null;
    @observable public isLoading: boolean = false;

    private readonly promoCodesState: PromoCodesState;
    public readonly configComponents: ConfigComponents;
    public readonly emailState: FormInputState<string, string>;
    public readonly passwordState: FormInputState<string, string>;
    public readonly promoCodeTextState: FormInputState<string, string>;
    public readonly welcomeStepFormModel: FormModel<WelcomeStepFormType>;
    private readonly localStorageState: LocalStorageState;
    public readonly affiliateIdModel: FormInputState<string, string | undefined>;

    public constructor(
        private readonly customKeyboard: CustomKeyboardState,
        private readonly moveToNextStage: () => void,
        private readonly common: Common,
        initialUserData?: WelcomeStepFormType
    ) {
        this.configComponents = ConfigComponents.get(this.common);

        this.localStorageState = LocalStorageState.get(this.common);

        this.promoCodesState = PromoCodesState.get(this.common);
        const initialEmail = initialUserData?.email === undefined ? '' : initialUserData.email;
        const initialPassword = initialUserData?.password === undefined ? '' : initialUserData.password;
        const initialPromoCode = this.promoCodeFromLocalStorage ?? '';
        const defaultPromoCodeFieldOpen = this.configComponents.config.defaultPromoCodeFieldOpen;
        this.isPromoCode = this.promoCodeFromLocalStorage === null ? defaultPromoCodeFieldOpen : true;

        this.emailState = FormInputState.new(initialEmail)
            .map(validateRequire)
            .map((value: string): Result<string> => {
                const valueTrim = value.trim();
                return Result.createOk(valueTrim);
            })
            .map(validateEmail);

        this.passwordState = FormInputState.new(initialPassword).map(validatePassword);

        this.promoCodeTextState = FormInputState.new(initialPromoCode);

        this.affiliateIdModel = this.promoCodeTextState.map((): Result<string | undefined> => {
            // TODO: needed ??
            return Result.createOk(undefined);
        });

        this.welcomeStepFormModel = FormModel.group({
            email: this.emailState,
            password: this.passwordState,
            promoCode: this.promoCodeTextState,
            //Remove in TMA-420
            affiliateId: this.affiliateIdModel,
        });

        makeObservable(this);
    }

    @computed public get promoCodeFromLocalStorage(): string | null {
        return this.localStorageState.promoCode.getValue();
    }

    @computed public get isCorrectEmail(): boolean {
        return validateEmailRaw(this.emailState.value);
    }

    @computed public get isCorrectPassword(): boolean {
        return checkPassword(this.passwordState.value);
    }

    @computed public get stepIsValid(): boolean {
        return (
            this.emailState.errorsForView.length === 0 &&
            this.passwordState.errorsForView.length === 0 &&
            this.emailState.value !== '' &&
            this.passwordState.value !== ''
        );
    }

    @action public promoCodeTextChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
        const value = e.currentTarget.value;
        const valueFromStorage = this.localStorageState.promoCode.getValue();
        if (value === '' && valueFromStorage !== null) {
            this.localStorageState.promoCode.setValue(null);
        }
    };

    @action public setPromoCode = (v: boolean): void => {
        this.isPromoCode = v;
    };

    @action public showPassword = (): void => {
        this.isShowPassword = !this.isShowPassword;
    };

    @action public onMoveToYourDetails = async (): Promise<void> => {
        this.customKeyboard.scrollAsideToTop();
        this.welcomeStepFormModel.setAsVisited();
        this.submitErrors = null;
        this.isLoading = true;

        try {
            if (this.welcomeStepFormModel.result.value.type === 'error') {
                return;
            }

            const { email, promoCode } = this.welcomeStepFormModel.result.value.data;

            const emailExists = await onValidateEmail(email);

            if (emailExists) {
                runInAction(() => {
                    this.submitErrors = getErrorByCode('ERROR_ALREADY_EXISTS');
                });
                return;
            }

            if (promoCode !== '') {
                const promoCodeResponse = await this.promoCodesState.checkPromoCode(promoCode);

                if (promoCodeResponse.correct === false) {
                    runInAction(() => {
                        this.submitErrors = promoCodeResponse.message;
                    });
                    return;
                }
            }

            this.moveToNextStage();
        } catch {
            runInAction(() => {
                this.submitErrors = 'Unknown error happened';
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };
}
